/**
 * Shared block registry — the SAME component is used by:
 *   - the visual editor preview (BuilderCanvas)
 *   - the public dynamic-page renderer (PageRenderer)
 * so what admins see equals what the world sees. No iframe magic.
 *
 * Each block declares:
 *   kind         — unique slug
 *   label        — human name
 *   group        — 'basic' | 'media' | 'layout' | 'dynamic' | 'form' | 'advanced'
 *   defaults     — default props for a fresh insert
 *   settings     — schema for the settings panel
 *   Component    — React component receiving { block, mode, data, style }
 *
 * `mode` is 'edit' | 'view'. In edit mode blocks render an inert version
 * that never fires clicks (so nested links/buttons don't fight DnD).
 */
import React from "react";
import HotDealsStrip from "@/components/deals/HotDealsStrip";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Search as SearchIcon,
  ArrowRight,
  MapPin,
  Users,
  ShieldCheck,
  Star,
  Play,
  ChevronDown,
  Check,
  X,
  Mail,
  Sparkles,
  Palette,
  Shapes,
  Mic,
  MessageCircle,
  PawPrint,
  Wrench,
  Paintbrush,
  Camera,
  Truck,
  Scissors,
  Home,
  Briefcase,
  Code,
  Globe,
  BookOpen,
  Music,
  Video,
  PenTool,
  Languages,
  Bot,
  HeadphonesIcon,
  Laptop,
  Flower2,
  Sofa,
  Package,
  Zap,
  Trees,
  Car,
  Heart,
  GraduationCap,
  Baby,
  Cake,
  Utensils,
  DollarSign,
  Layers,
  FolderOpen,
} from "lucide-react";

const cx = (...c) => c.filter(Boolean).join(" ");

// ----- SIMPLE / TEXT BLOCKS ---------------------------------------------

const HeadingBlock = ({ block }) => {
  const { level = "h2", text = "Heading", align = "left" } = block.props || {};
  const tone = level === "eyebrow"
    ? "text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]"
    : level === "h1"
    ? "font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#071b22]"
    : level === "h2"
    ? "font-display text-3xl sm:text-4xl font-semibold text-[#071b22]"
    : level === "h3"
    ? "font-display text-2xl font-semibold text-[#071b22]"
    : "font-display text-xl font-semibold text-[#071b22]";
  const Tag = level === "eyebrow" ? "div" : level;
  const alignCls = align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
  return <Tag className={cx(tone, alignCls, "my-2")}>{text}</Tag>;
};

const TextBlock = ({ block }) => {
  const { body = "", role, align = "left" } = block.props || {};
  const alignCls = align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
  if (role === "note") {
    return (
      <p className={cx("text-xs text-[#252A34]/60 italic border-l-2 border-slate-200 pl-3 my-3", alignCls)}>{body}</p>
    );
  }
  return <p className={cx("text-[#252A34]/85 leading-relaxed my-3", alignCls)}>{body}</p>;
};

const ButtonBlock = ({ block, mode }) => {
  const { label = "Click me", href = "#", variant = "primary" } = block.props || {};
  const cls = variant === "primary"
    ? "bg-[#165a71] text-white hover:bg-[#11485a]"
    : variant === "secondary"
    ? "bg-white border border-[#165a71] text-[#165a71] hover:bg-[#165a71]/5"
    : "bg-transparent text-[#165a71] underline";
  const inner = <span className={cx("inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium", cls)}>{label} <ArrowRight className="h-3.5 w-3.5" /></span>;
  if (mode === "edit") return <span data-testid={`block-button-${block.id}`}>{inner}</span>;
  return <Link to={href} data-testid={`block-button-${block.id}`}>{inner}</Link>;
};

const SpacerBlock = ({ block }) => {
  const { size = "md" } = block.props || {};
  const h = size === "xs" ? "h-4" : size === "sm" ? "h-8" : size === "md" ? "h-16" : size === "lg" ? "h-24" : "h-32";
  return <div className={h} aria-hidden />;
};

const DividerBlock = () => <hr className="my-8 border-slate-200" />;

const ImageBlock = ({ block }) => {
  const { src, alt = "", caption, radius = "lg" } = block.props || {};
  if (!src) return <div className="rounded-lg border-2 border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">[Image] Click to select a media file.</div>;
  const rcls = radius === "none" ? "" : radius === "sm" ? "rounded-md" : radius === "md" ? "rounded-lg" : radius === "lg" ? "rounded-2xl" : "rounded-3xl";
  return (
    <figure className="my-4">
      <img src={src} alt={alt} className={cx("w-full h-auto object-cover", rcls)} loading="lazy" />
      {caption && <figcaption className="text-xs text-[#252A34]/60 mt-2 text-center">{caption}</figcaption>}
    </figure>
  );
};

const GalleryBlock = ({ block }) => {
  const { items = [], columns = 3 } = block.props || {};
  const gridCls = columns === 2 ? "grid-cols-2" : columns === 4 ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-3";
  return (
    <div className={cx("grid gap-3 my-4", gridCls)}>
      {items.length === 0 && <div className="col-span-full rounded-lg border-2 border-dashed border-slate-300 p-6 text-sm text-slate-500 text-center">Add images to this gallery.</div>}
      {items.map((it, i) => (
        <img key={i} src={it.src} alt={it.alt || ""} className="w-full aspect-square object-cover rounded-lg" loading="lazy" />
      ))}
    </div>
  );
};

const VideoBlock = ({ block }) => {
  const { url, provider = "youtube" } = block.props || {};
  if (!url) return <div className="rounded-lg border-2 border-dashed border-slate-300 p-8 text-center text-sm text-slate-500"><Play className="h-6 w-6 mx-auto mb-2" /> Paste a YouTube or Vimeo URL.</div>;
  let embed = url;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (ytMatch) embed = `https://www.youtube.com/embed/${ytMatch[1]}`;
  return (
    <div className="my-4 aspect-video">
      <iframe src={embed} title="Video" allow="autoplay; encrypted-media" allowFullScreen className="w-full h-full rounded-2xl" />
    </div>
  );
};

const CardsBlock = ({ block }) => {
  const { cards = [], columns = 3 } = block.props || {};
  const gridCls = columns === 2 ? "grid-cols-1 md:grid-cols-2" : columns === 4 ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 md:grid-cols-3";
  return (
    <div className={cx("grid gap-6 my-6", gridCls)}>
      {cards.map((c, i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-[#165a71] mb-2">
            {c.icon && <IconByName name={c.icon} className="h-6 w-6" />}
          </div>
          <div className="font-display font-semibold text-[#071b22] mb-1">{c.title}</div>
          <div className="text-sm text-[#252A34]/75">{c.body}</div>
        </div>
      ))}
    </div>
  );
};

const AccordionBlock = ({ block }) => {
  const { items = [] } = block.props || {};
  const [open, setOpen] = React.useState(-1);
  return (
    <div className="my-4 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
      {items.map((it, i) => (
        <div key={i}>
          <button
            type="button"
            className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
            onClick={() => setOpen(open === i ? -1 : i)}
            data-testid={`accordion-item-${i}`}
          >
            <span className="font-medium text-[#071b22]">{it.q || it.question}</span>
            <ChevronDown className={cx("h-4 w-4 transition", open === i && "rotate-180")} />
          </button>
          {open === i && (
            <div className="px-5 pb-4 text-sm text-[#252A34]/80 leading-relaxed">{it.a || it.answer}</div>
          )}
        </div>
      ))}
    </div>
  );
};

const CTABlock = ({ block, mode }) => {
  const { headline = "", body = "", primary_cta, secondary_cta, background = "coastal-blue" } = block.props || {};
  const bg = background === "coastal-blue"
    ? "bg-[#165a71] text-white"
    : background === "deep-sea"
    ? "bg-[#071b22] text-white"
    : "bg-white text-[#071b22] border border-slate-200";
  return (
    <div className={cx("rounded-3xl px-6 py-10 md:px-10 md:py-14 my-6", bg)}>
      <h3 className="font-display text-2xl md:text-3xl font-semibold max-w-2xl">{headline}</h3>
      {body && <p className={cx("mt-3 max-w-2xl", background === "white" ? "text-[#252A34]/75" : "text-white/80")}>{body}</p>}
      <div className="mt-6 flex flex-wrap gap-3">
        {primary_cta?.label && (mode === "edit"
          ? <span className="inline-flex items-center gap-2 rounded-full bg-white text-[#165a71] px-5 py-2.5 text-sm font-medium">{primary_cta.label}</span>
          : <Link to={primary_cta.href || "#"} className="inline-flex items-center gap-2 rounded-full bg-white text-[#165a71] px-5 py-2.5 text-sm font-medium hover:bg-white/90">{primary_cta.label}</Link>)}
        {secondary_cta?.label && (mode === "edit"
          ? <span className="inline-flex items-center gap-2 rounded-full border border-white/50 text-white px-5 py-2.5 text-sm font-medium">{secondary_cta.label}</span>
          : <Link to={secondary_cta.href || "#"} className="inline-flex items-center gap-2 rounded-full border border-white/50 text-white px-5 py-2.5 text-sm font-medium hover:bg-white/10">{secondary_cta.label}</Link>)}
      </div>
    </div>
  );
};

const CustomHTMLBlock = ({ block }) => {
  const { html = "" } = block.props || {};
  return <div className="my-4" dangerouslySetInnerHTML={{ __html: html }} />;
};

// ----- HERO WITH SEARCH (protected system block) -------------------------

const HeroSearchBlock = ({ block, mode }) => {
  const { eyebrow, headline, subheadline, search_placeholder, location_placeholder, primary_cta, secondary_cta, tertiary_cta } = block.props || {};
  const [q, setQ] = React.useState("");
  const [loc, setLoc] = React.useState("");
  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#071b22] via-[#0b2a35] to-[#165a71] text-white p-6 md:p-12 my-4">
      <div className="max-w-3xl relative z-10">
        {eyebrow && <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#8fd8ea] mb-3">{eyebrow}</div>}
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight">{headline}</h1>
        {subheadline && <p className="mt-4 text-white/80 text-base md:text-lg max-w-2xl">{subheadline}</p>}
        <form
          className="mt-8 flex flex-col md:flex-row gap-2 bg-white rounded-2xl p-2 shadow-2xl"
          data-testid="hero-search-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (mode === "edit") return;
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (loc) params.set("location", loc);
            window.location.href = `/services?${params.toString()}`;
          }}
        >
          <div className="flex-1 relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} className="w-full pl-9 pr-3 py-3 text-sm text-slate-900 rounded-xl focus:outline-none" placeholder={search_placeholder || "What do you need done?"} data-testid="hero-q-input" />
          </div>
          <div className="flex-1 relative border-t md:border-t-0 md:border-l border-slate-100">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={loc} onChange={(e) => setLoc(e.target.value)} className="w-full pl-9 pr-3 py-3 text-sm text-slate-900 rounded-xl focus:outline-none" placeholder={location_placeholder || "Enter city or ZIP"} data-testid="hero-loc-input" />
          </div>
          <button type="submit" className="rounded-xl bg-[#165a71] text-white px-6 py-3 text-sm font-medium hover:bg-[#11485a]" data-testid="hero-search-btn">Search</button>
        </form>
        <div className="mt-4 flex flex-wrap gap-2">
          {[primary_cta, secondary_cta, tertiary_cta].filter(Boolean).map((cta, i) => {
            const isTertiary = i === 2;
            const inner = (
              <span className={cx("inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium", isTertiary ? "border border-white/40 text-white" : "bg-white/10 text-white border border-white/20")}>
                {cta.label} <ArrowRight className="h-3 w-3" />
              </span>
            );
            const href = cta.kind === "local" ? "/services?kind=local" : cta.kind === "digital" ? "/services?kind=digital" : "/request-a-service";
            return mode === "edit"
              ? <span key={i} data-testid={`hero-cta-${i}`}>{inner}</span>
              : <Link key={i} to={href} data-testid={`hero-cta-${i}`}>{inner}</Link>;
          })}
        </div>
      </div>
    </div>
  );
};

// ----- DYNAMIC / DATA-BOUND BLOCKS --------------------------------------
// These fetch from the public API and render live content. Reused in the
// editor with a "preview only" note so the query is legible.

// Premium first-class marketing section components — imported so admins
// can drop them into the builder without decomposing into primitives.
import {
  GcwHeroSection,
  GcwStatsStrip,
  GcwCategoriesSection,
  GcwFeaturedProviders,
  GcwHowItWorks,
  GcwBecomeProviderCta,
  GcwTrustSection,
} from "@/components/marketing/HomeSections";
import { RequestHero, RequestServiceGrid, AiGuidedRequest, RequestDeals, RequestCta } from "@/components/marketing/RequestSections";
import { RequestSteps, TrustStrip, GuaranteePanel } from "@/components/marketing/RequestPremium";
import { PageHero, SplitFeature, FeatureGrid, StepsTimeline, StatsBand, QuoteBand, ImageCta, FaqSection, SectionHeader, ChannelCards } from "@/components/marketing/ContentSections";

function useApiList(url, params) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api.get(url, { params }).then(r => { if (alive) setData(r.data); }).catch(e => { if (alive) setError(e); });
    return () => { alive = false; };
  }, [url, JSON.stringify(params || {})]);
  return { data, error };
}

const CategoryGridBlock = ({ block, mode }) => {
  const { query = {}, columns = 4 } = block.props || {};
  const { data } = useApiList("/categories", { featured: query.featured_only ? true : undefined, limit: query.limit || 12 });
  const items = data || [];
  const gridCls = columns === 3 ? "grid-cols-2 md:grid-cols-3" : columns === 6 ? "grid-cols-3 md:grid-cols-6" : "grid-cols-2 md:grid-cols-4";
  return (
    <div className={cx("grid gap-3 my-4", gridCls)} data-testid={`category-grid-${block.id}`}>
      {items.slice(0, query.limit || 12).map((c) => {
        const inner = (
          <div className="group rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#165a71]/50 hover:shadow-sm transition cursor-pointer">
            <div className="text-[#165a71] mb-2"><IconByName name={c.icon} className="h-6 w-6" /></div>
            <div className="font-medium text-[#071b22] text-sm">{c.name}</div>
            <div className="text-xs text-[#252A34]/60 mt-1">{c.provider_count || ""}</div>
          </div>
        );
        return mode === "edit"
          ? <div key={c.id}>{inner}</div>
          : <Link key={c.id} to={`/categories/${c.slug}`}>{inner}</Link>;
      })}
      {items.length === 0 && <div className="col-span-full text-sm text-[#252A34]/60 text-center py-6">No categories yet.</div>}
    </div>
  );
};

const ProviderGridBlock = ({ block, mode }) => {
  const { query = {}, columns = 3 } = block.props || {};
  const params = {
    limit: query.limit || 6,
    sort: query.sort || "-rating_avg",
    min_rating: query.min_rating,
    category: query.category === ":slug" ? undefined : query.category,
    location: query.location === ":slug" ? undefined : query.location,
  };
  const { data } = useApiList("/providers", params);
  const items = (data && data.items) || data || [];
  const gridCls = columns === 2 ? "grid-cols-1 md:grid-cols-2" : columns === 4 ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
  return (
    <div className={cx("grid gap-4 my-4", gridCls)} data-testid={`provider-grid-${block.id}`}>
      {items.slice(0, query.limit || 6).map((p) => {
        const inner = (
          <div className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#165a71]/50 hover:shadow-md transition">
            <div className="flex items-start gap-3">
              {p.profile_image_url && <img src={p.profile_image_url} alt="" className="h-12 w-12 rounded-full object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-[#071b22] truncate">{p.display_name || p.business_name || "Provider"}</div>
                <div className="text-xs text-[#252A34]/60 truncate">{p.title || p.category_names?.[0] || ""}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs text-[#252A34]/70">
              <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 text-amber-500" />{(p.rating_avg || 0).toFixed(1)}</span>
              <span>{p.completed_jobs_count || 0} jobs</span>
              {p.is_verified && <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="h-3 w-3" />Verified</span>}
            </div>
          </div>
        );
        return mode === "edit"
          ? <div key={p.id}>{inner}</div>
          : <Link key={p.id} to={`/providers/${p.slug || p.id}`}>{inner}</Link>;
      })}
      {items.length === 0 && <div className="col-span-full text-sm text-[#252A34]/60 text-center py-6">No providers to show.</div>}
    </div>
  );
};

const ServiceGridBlock = ({ block, mode }) => {
  const { query = {}, columns = 3 } = block.props || {};
  const params = { limit: query.limit || 6, sort: query.sort || "-rating_avg", kind: query.kind, category: query.category === ":slug" ? undefined : query.category, location: query.location === ":slug" ? undefined : query.location };
  const { data } = useApiList("/services", params);
  const items = (data && data.items) || data || [];
  const gridCls = columns === 2 ? "grid-cols-1 md:grid-cols-2" : columns === 4 ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
  return (
    <div className={cx("grid gap-4 my-4", gridCls)} data-testid={`service-grid-${block.id}`}>
      {items.slice(0, query.limit || 6).map((s) => {
        const inner = (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-[#165a71]/50 hover:shadow-md transition">
            {s.hero_image_url && <img src={s.hero_image_url} alt="" className="w-full h-32 object-cover" />}
            <div className="p-4">
              <div className="font-display font-semibold text-[#071b22] line-clamp-2">{s.title}</div>
              <div className="text-xs text-[#252A34]/60 mt-1">{s.category_name || ""}</div>
              <div className="mt-2 text-sm text-[#165a71] font-medium">{s.starting_price ? `From $${s.starting_price}` : "Quote on request"}</div>
            </div>
          </div>
        );
        return mode === "edit"
          ? <div key={s.id}>{inner}</div>
          : <Link key={s.id} to={`/services/${s.slug || s.id}`}>{inner}</Link>;
      })}
      {items.length === 0 && <div className="col-span-full text-sm text-[#252A34]/60 text-center py-6">No services to show.</div>}
    </div>
  );
};

const PricingPlansBlock = ({ block, mode }) => {
  const { audience = "providers" } = block.props || {};
  const { data } = useApiList("/plans", {});
  const plans = data || [];
  const filtered = audience === "providers" ? plans : plans.filter(p => p.audience === audience);
  // Features may be stored as a string[] OR a dict {feature_key: value}. Normalise.
  const featureList = (p) => {
    if (!p.features) return [];
    if (Array.isArray(p.features)) return p.features;
    if (typeof p.features === "object") {
      return Object.entries(p.features).map(([k, v]) => {
        const label = k.replace(/_/g, " ");
        if (v === true) return label.charAt(0).toUpperCase() + label.slice(1);
        if (v === false) return null;
        return `${label.charAt(0).toUpperCase() + label.slice(1)}: ${v}`;
      }).filter(Boolean);
    }
    return [];
  };
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 my-6" data-testid={`pricing-plans-${block.id}`}>
      {filtered.map((p) => (
        <div key={p.key} className={cx("relative rounded-3xl border bg-white p-6 flex flex-col transition-transform hover:-translate-y-1", p.key === "pro" ? "border-[#165a71] ring-1 ring-[#165a71] shadow-[0_24px_48px_-24px_rgba(22,90,113,0.5)]" : "border-slate-200")} data-testid={`plan-card-${p.key}`}>
          {p.key === "pro" && <span className="absolute -top-3 left-6 rounded-full bg-[#165a71] text-white text-[11px] font-semibold px-3 py-1">Most popular</span>}
          <div className="font-display text-lg font-semibold text-[#071b22]">{p.name}</div>
          <div className="mt-2 text-3xl font-display font-semibold">
            ${p.monthly_price ?? p.price_monthly ?? 0}<span className="text-sm text-[#252A34]/60 font-normal">/mo</span>
          </div>
          {p.description && <p className="text-sm text-[#252A34]/70 mt-2 line-clamp-2">{p.description}</p>}
          <ul className="mt-4 space-y-2 text-sm text-[#252A34]/80 flex-1">
            {featureList(p).slice(0, 8).map((f, i) => (
              <li key={i} className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />{f}</li>
            ))}
          </ul>
          {mode === "edit"
            ? <span className="mt-5 inline-flex items-center justify-center rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium">Choose {p.name}</span>
            : <Link to="/dashboard/plans" className="mt-5 inline-flex items-center justify-center rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium hover:bg-[#11485a]">Choose {p.name}</Link>}
        </div>
      ))}
    </div>
  );
};

const BlogPostsBlock = ({ block, mode }) => {
  const { limit = 3, category, columns = 3 } = block.props || {};
  const { data } = useApiList("/content/blog", { limit, category });
  const items = data || [];
  const gridCls = columns === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-3";
  return (
    <div className={cx("grid gap-4 my-4", gridCls)}>
      {items.slice(0, limit).map((p) => {
        const inner = (
          <article className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-md transition">
            {p.hero_image_url && <img src={p.hero_image_url} alt="" className="w-full h-40 object-cover" />}
            <div className="p-5">
              <div className="text-xs text-[#165a71] uppercase font-semibold tracking-wider">{p.category}</div>
              <div className="font-display font-semibold text-[#071b22] mt-1 line-clamp-2">{p.title}</div>
              <div className="text-sm text-[#252A34]/70 mt-2 line-clamp-2">{p.excerpt}</div>
            </div>
          </article>
        );
        return mode === "edit"
          ? <div key={p.id}>{inner}</div>
          : <Link key={p.id} to={`/blog/${p.slug}`}>{inner}</Link>;
      })}
    </div>
  );
};

const HelpArticlesBlock = ({ block, mode }) => {
  const { limit = 20, layout = "list", category, audience } = block.props?.query || block.props || {};
  const { data } = useApiList("/content/help", { limit, category, audience });
  const items = data || [];
  // Group by category
  const groups = {};
  items.forEach((a) => {
    const k = a.category || "General";
    (groups[k] = groups[k] || []).push(a);
  });
  return (
    <div className="my-4 grid gap-6 md:grid-cols-2" data-testid={`help-articles-${block.id}`}>
      {Object.entries(groups).map(([cat, arts]) => (
        <div key={cat} className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="font-display font-semibold text-[#071b22] mb-3">{cat}</div>
          <ul className="space-y-2">
            {arts.map((a) => (
              <li key={a.id}>
                {mode === "edit"
                  ? <span className="text-sm text-[#165a71]">{a.title}</span>
                  : <Link to={`/help/${a.slug}`} className="text-sm text-[#165a71] hover:underline">{a.title}</Link>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

const FaqListBlock = ({ block }) => {
  const { audience, category, limit = 20 } = block.props?.query || block.props || {};
  const { data } = useApiList("/content/faqs", { audience, category });
  const items = (data || []).slice(0, limit);
  const asAccordion = { props: { items: items.map((f) => ({ q: f.question, a: f.answer })) } };
  return <AccordionBlock block={{ ...block, ...asAccordion }} />;
};

// ----- FORM BLOCK --------------------------------------------------------

const FormBlock = ({ block, mode }) => {
  const { title, fields = [], submit_label = "Submit", form_key = "contact", success_message = "Thanks!" } = block.props || {};
  const [values, setValues] = React.useState({});
  const [state, setState] = React.useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (mode === "edit") return;
    setState("submitting");
    try {
      await api.post("/form-submissions", { form_key, fields: values });
      setState("done");
    } catch (err) {
      setState("error");
    }
  };
  if (state === "done") {
    return <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-8 min-h-[240px] flex items-center justify-center text-center text-emerald-900" data-testid={`form-success-${block.id}`}>{success_message}</div>;
  }
  return (
    <form className="rounded-2xl border border-slate-200 bg-white p-6 my-4" onSubmit={submit} data-testid={`form-block-${block.id}`}>
      {title && <h3 className="font-display text-xl font-semibold text-[#071b22] mb-4">{title}</h3>}
      <div className="grid gap-4">
        {fields.map((f) => (
          <div key={f.name}>
            <label className="block text-xs font-medium text-[#252A34] mb-1">{f.label}</label>
            {f.type === "textarea"
              ? <textarea rows={f.rows || 4} required={f.required} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#165a71] focus:outline-none" data-testid={`form-field-${f.name}`} />
              : f.type === "select"
              ? <select required={f.required} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-[#165a71] focus:outline-none" data-testid={`form-field-${f.name}`}>
                  <option value="">Choose one</option>
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              : <input type={f.type || "text"} required={f.required} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#165a71] focus:outline-none" data-testid={`form-field-${f.name}`} />}
          </div>
        ))}
      </div>
      <button type="submit" disabled={state === "submitting"} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#11485a] disabled:opacity-60" data-testid={`form-submit-${block.id}`}>
        {state === "submitting" ? "Sending…" : submit_label}
      </button>
      {state === "error" && <div className="mt-3 text-sm text-red-600">Something went wrong. Please try again.</div>}
    </form>
  );
};

// ----- PREMIUM MARKETING SECTIONS (first-class, editable) ---------------
// These wrap the hand-built HomeSections components. They render at full
// section width (they include their own <section>/max-w container), so they
// should be placed inside a section whose layout is 'full-bleed' — the
// SectionRenderer respects that by not adding its own container.

const HeroPremiumBlock = ({ block, mode }) => <GcwHeroSection props={block.props} mode={mode} />;
const StatsStripBlock = ({ block, mode }) => <GcwStatsStrip props={block.props} mode={mode} />;
const HomeCategoriesBlock = ({ block, mode }) => <GcwCategoriesSection props={block.props} mode={mode} />;
const HotDealsBlock = ({ block }) => <div className="mx-auto max-w-7xl px-6 lg:px-10"><HotDealsStrip title={block.props?.headline || "Hot deals on the coast"} className="py-16" /></div>;
const FeaturedProvidersBlock = ({ block, mode }) => <GcwFeaturedProviders props={block.props} mode={mode} />;
const HowItWorksBlock = ({ block, mode }) => <GcwHowItWorks props={block.props} mode={mode} />;
const BecomeProviderCtaBlock = ({ block, mode }) => <GcwBecomeProviderCta props={block.props} mode={mode} />;
const TrustSectionBlock = ({ block, mode }) => <GcwTrustSection props={block.props} mode={mode} />;

// ----- PREMIUM CONTENT-PAGE SECTIONS ------------------------------------
const PageHeroBlock = ({ block, mode }) => <PageHero props={block.props} mode={mode} />;
const SplitFeatureBlock = ({ block, mode }) => <SplitFeature props={block.props} mode={mode} />;
const FeatureGridBlock = ({ block }) => <FeatureGrid props={block.props} />;
const StepsTimelineBlock = ({ block, mode }) => <StepsTimeline props={block.props} mode={mode} />;
const StatsBandBlock = ({ block }) => <StatsBand props={block.props} />;
const QuoteBandBlock = ({ block }) => <QuoteBand props={block.props} />;
const ImageCtaBlock = ({ block, mode }) => <ImageCta props={block.props} mode={mode} />;
const FaqSectionBlock = ({ block }) => <FaqSection props={block.props} />;
const PlansSectionBlock = ({ block, mode }) => {
  const { eyebrow, headline, lede, audience = "providers" } = block.props || {};
  return (
    <section className="bg-slate-50 py-20 md:py-28" data-testid="plans-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeader eyebrow={eyebrow} headline={headline} lede={lede} center />
        <div className="mt-10"><PricingPlansBlock block={{ ...block, props: { audience } }} mode={mode} /></div>
      </div>
    </section>
  );
};
const ContactSectionBlock = ({ block, mode }) => {
  const { eyebrow, headline, lede, channels = [], form = {} } = block.props || {};
  return (
    <section className="bg-slate-50 py-20 md:py-28" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-12 lg:grid-cols-[1fr_1.05fr] items-start">
        <div><SectionHeader eyebrow={eyebrow} headline={headline} lede={lede} /><ChannelCards items={channels} /></div>
        <div className="rounded-[2rem] bg-white p-2 shadow-[0_30px_60px_-30px_rgba(7,27,34,0.3)] ring-1 ring-slate-200/80 [&_form]:border-0 [&_form]:my-0">
          <FormBlock block={{ ...block, props: form }} mode={mode} />
        </div>
      </div>
    </section>
  );
};

// ----- REUSABLE BLOCK REFERENCE ------------------------------------------
// Renders the payload of a reusable/global block resolved at render-time.
// Cached in-memory so multiple ref instances on the same page hit the API once.
const _REUSABLE_CACHE = new Map();
async function _fetchReusable(rid) {
  if (_REUSABLE_CACHE.has(rid)) return _REUSABLE_CACHE.get(rid);
  const promise = api.get("/reusable-blocks").then(r => (r.data || []).find(rb => rb.id === rid) || null).catch(() => null);
  _REUSABLE_CACHE.set(rid, promise);
  return promise;
}
const ReusableRefBlock = ({ block, mode }) => {
  const { reusable_id, fallback_label = "Reusable block" } = block.props || {};
  const [resolved, setResolved] = React.useState(null);
  const [missing, setMissing] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    if (!reusable_id) { setMissing(true); return; }
    _fetchReusable(reusable_id).then((rb) => {
      if (!alive) return;
      if (!rb) setMissing(true);
      else setResolved(rb);
    });
    return () => { alive = false; };
  }, [reusable_id]);
  if (missing) {
    return (
      <div className="rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
        Reusable block not found: {fallback_label}
      </div>
    );
  }
  if (!resolved) {
    return <div className="my-4 h-24 rounded-xl bg-slate-100 animate-pulse" data-testid="reusable-loading" />;
  }
  // payload may be either a single block or a section-like object with blocks.
  const payload = resolved.payload || {};
  const inlineBlocks = payload.blocks && Array.isArray(payload.blocks) ? payload.blocks : (payload.kind ? [payload] : []);
  return (
    <div data-testid={`reusable-${resolved.id}`}>
      {inlineBlocks.map((b, i) => (
        <div key={b.id || i}>{renderBlock(b, mode)}</div>
      ))}
    </div>
  );
};

// ----- Registry ---------------------------------------------------------

export const BLOCK_REGISTRY = {
  // === PREMIUM MARKETING SECTIONS ===
  hero_premium: { label: "Hero + search (premium)", group: "premium", Component: HeroPremiumBlock, protected: true, fullBleed: true, defaults: {
    eyebrow: "Serving the Gulf Coast — FL · AL · MS · LA · TX",
    headline_prefix: "Get Any Kind of",
    headline_accent: "Work Done.",
    subheadline: "Book local professionals, hire digital experts, or post a custom project — from one trusted marketplace built for the Gulf Coast.",
    background_image: "https://images.unsplash.com/photo-1600210492493-0946911123ea?auto=format&fit=crop&w=2000&q=80",
    ctas: [
      { key: "local", icon: "map-pin", title: "Book Local", subtitle: "Cleaners, handymen, movers, lawn care — same day.", href: "/book-local", accent: "from-[#165a71] to-[#0f3d4d]" },
      { key: "digital", icon: "sparkles", title: "Hire Digital", subtitle: "Designers, developers, marketers, editors.", href: "/hire-digital", accent: "from-[#252A34] to-[#071b22]" },
      { key: "request", icon: "message-square", title: "Request a Service", subtitle: "Describe the job — pros come to you with quotes.", href: "/request-a-service", accent: "from-[#37768e] to-[#165a71]", requires_auth: true },
    ],
  }},
  stats_strip: { label: "Live stats strip", group: "premium", Component: StatsStripBlock, fullBleed: true, defaults: {
    stat_endpoint: "/system/stats",
    labels: { providers: "Verified pros", categories: "Service categories", locations: "Gulf Coast locations", users: "Community members" },
  }},
  home_categories: { label: "Popular categories", group: "premium", Component: HomeCategoriesBlock, fullBleed: true, defaults: {
    eyebrow: "Explore", headline: "Popular categories on the coast",
    cta_label: "All categories", cta_href: "/categories",
    limit: 12, show_local: true, show_digital: true,
  }},
  home_hot_deals: { label: "Hot deals strip", group: "premium", Component: HotDealsBlock, fullBleed: true, defaults: {
    headline: "Hot deals on the coast",
  }},
  home_featured_providers: { label: "Featured providers", group: "premium", Component: FeaturedProvidersBlock, fullBleed: true, defaults: {
    eyebrow: "Featured pros", headline: "Meet Gulf Coast standouts",
    cta_label: "Browse all pros", cta_href: "/providers",
    limit: 8, sort: "featured",
  }},
  home_how_it_works: { label: "How it works", group: "premium", Component: HowItWorksBlock, fullBleed: true, defaults: {
    eyebrow: "How it works",
    headline: "One marketplace. Three ways to get things done.",
    lede: "Whether it's a leaking faucet in Pensacola or a rebrand for your Baton Rouge coffee shop — Gulf Coast Work keeps the whole workflow (search, hire, chat, pay, review) in one place.",
    cta_label: "Create your free account", cta_href: "/register",
    steps: [
      { icon: "users", title: "Tell us what you need", copy: "Search a category, describe the job, or start with a template. Real, verified pros on both sides — no lead-broker games." },
      { icon: "wallet", title: "Book, request quotes, or hire", copy: "Instant booking for local pros, custom quotes for projects, or long-form gigs like Fiverr — all under one roof." },
      { icon: "shield-check", title: "Get it done — with peace of mind", copy: "Escrow-protected payments, ID-verified pros, ratings you can trust, and dispute support if anything goes sideways." },
    ],
  }},
  home_become_provider_cta: { label: "Become a provider (dark CTA)", group: "premium", Component: BecomeProviderCtaBlock, fullBleed: true, defaults: {
    eyebrow: "For pros",
    headline: "Get discovered by real customers on the coast.",
    body: "Whether you clean pool decks in Destin or design brands from Baton Rouge — build a profile once, and get booked from every angle.",
    primary_cta: { label: "Become a provider", href: "/register?intended_role=provider" },
    secondary_cta: { label: "How it works", href: "/become-a-provider" },
    callouts: [
      { icon: "zap", title: "Zero listing fees", copy: "Only pay when you actually get hired." },
      { icon: "shield-check", title: "Buyer-protected", copy: "Escrow, insurance & dispute support." },
      { icon: "users", title: "One profile", copy: "Local, digital or agency — unified." },
      { icon: "sparkles", title: "Free growth tools", copy: "Promotions, referrals & featured slots." },
    ],
  }},
  home_trust_section: { label: "Trust & verification", group: "premium", Component: TrustSectionBlock, fullBleed: true, defaults: {} },

  // === PREMIUM CONTENT-PAGE SECTIONS ===
  page_hero: { label: "Page hero with image", group: "premium", Component: PageHeroBlock, fullBleed: true, defaults: { eyebrow: "Eyebrow", headline: "A clear, confident headline", accent: "with an accent.", body: "One or two sentences that explain the page.", image: "", primary_cta: { label: "Get started", href: "/register" }, secondary_cta: { label: "Learn more", href: "/about" }, highlights: [{ value: "5", label: "Gulf states" }], badge: { icon: "shield-check", title: "Verified pros", body: "ID-checked before listing" } } },
  split_feature: { label: "Image + text split", group: "premium", Component: SplitFeatureBlock, fullBleed: true, defaults: { eyebrow: "Eyebrow", headline: "Headline", body: "Supporting copy.", bullets: ["First point", "Second point"], image: "", reverse: false, tone: "white", cta: { label: "", href: "/" } } },
  feature_grid: { label: "Feature cards grid", group: "premium", Component: FeatureGridBlock, fullBleed: true, defaults: { eyebrow: "Eyebrow", headline: "Headline", lede: "", columns: 3, tone: "white", items: [{ icon: "shield-check", title: "Feature", body: "Description" }] } },
  steps_timeline: { label: "Numbered steps", group: "premium", Component: StepsTimelineBlock, fullBleed: true, defaults: { eyebrow: "How it works", headline: "Three simple steps", lede: "", image: "", tone: "light", steps: [{ title: "Step one", body: "Description" }] } },
  stats_band: { label: "Stats band", group: "premium", Component: StatsBandBlock, fullBleed: true, defaults: { items: [{ value: "5", label: "Gulf states" }] } },
  quote_band: { label: "Quote / testimonial", group: "premium", Component: QuoteBandBlock, fullBleed: true, defaults: { quote: "A short quote.", name: "Name", role: "Role", image: "" } },
  image_cta: { label: "Image call to action", group: "premium", Component: ImageCtaBlock, fullBleed: true, defaults: { eyebrow: "", headline: "Ready to start?", body: "", image: "", primary_cta: { label: "Get started", href: "/register" }, secondary_cta: { label: "", href: "/" } } },
  faq_section: { label: "FAQ section", group: "premium", Component: FaqSectionBlock, fullBleed: true, defaults: { eyebrow: "FAQ", headline: "Common questions", items: [{ q: "Question", a: "Answer" }] } },
  plans_section: { label: "Pricing plans section", group: "premium", Component: PlansSectionBlock, fullBleed: true, defaults: { eyebrow: "Plans", headline: "Pick a plan", lede: "", audience: "providers" } },
  contact_section: { label: "Contact + form section", group: "premium", Component: ContactSectionBlock, fullBleed: true, defaults: { eyebrow: "Contact", headline: "Get in touch", lede: "", channels: [{ icon: "mail", title: "Email", body: "hello@example.com" }], form: { title: "Send us a message", form_key: "contact", fields: [{ name: "name", label: "Your name", type: "text", required: true }, { name: "email", label: "Email", type: "email", required: true }, { name: "message", label: "Message", type: "textarea", required: true }], submit_label: "Send message", success_message: "Thanks — we'll be in touch within one business day." } } },

  // === REQUEST-FIRST SECTIONS ===
  request_hero: { label: "Request hero (AI assistant)", group: "request", Component: ({ block, mode }) => <RequestHero props={block.props} mode={mode} />, fullBleed: true, defaults: { headline: "What Do You Need Done?", body: "Tell us what you need and we'll connect you with qualified Gulf Coast professionals.", placeholders: ["Repair my leaking sink", "Fix my air conditioner"], primary_label: "Get Estimates", secondary_cta: { label: "Browse Services", href: "/services" }, ask_location: true, trust_text: "Free to request • No obligation", image: "", request_source: "homepage_hero" } },
  request_service_grid: { label: "Request service grid", group: "request", Component: ({ block, mode }) => <RequestServiceGrid props={block.props} mode={mode} />, fullBleed: true, defaults: { eyebrow: "", headline: "What can we help you with?", lede: "", data_source: "custom", items: [{ label: "House Cleaning", category_slug: "house-cleaning", icon: "sparkles", description: "", click_behavior: "request", urgency: "", deal_id: "", campaign: "", url: "" }], columns: 4, card_style: "icon", tone: "white", request_source: "category", mobile_slider: true, limit: 12, cta: { label: "", href: "" } } },
  ai_guided_request: { label: "AI guided request", group: "request", Component: ({ block }) => <AiGuidedRequest props={block.props} />, fullBleed: true, defaults: { eyebrow: "AI guided request", headline: "Not sure what service you need?", body: "Describe the problem in your own words.", placeholder: "My bathroom ceiling has water damage…", button_label: "Help Me Find the Right Pro", request_source: "ai_search" } },
  request_deals: { label: "Deal-driven requests", group: "request", Component: ({ block, mode }) => <RequestDeals props={block.props} mode={mode} />, fullBleed: true, defaults: { eyebrow: "Savings", headline: "Save on your next request", lede: "", limit: 4, request_source: "deal" } },
  request_cta: { label: "Request Service CTA", group: "request", Component: ({ block, mode }) => <RequestCta props={block.props} mode={mode} />, fullBleed: true, defaults: { heading: "Need it done this week?", description: "Tell us what you need and get estimates.", icon: "sparkles", button_label: "Request a Service", category_slug: "", urgency: "", deal_id: "", campaign: "", tone: "sand", request_source: "cta" } },
  request_steps: { label: "Visual how-it-works steps", group: "request", Component: ({ block, mode }) => <RequestSteps props={block.props} mode={mode} />, fullBleed: true, defaults: { eyebrow: "How it works", headline: "From request to booked", lede: "", tone: "white", cta: { label: "Start Your Request" }, steps: [{ title: "Tell us what you need", body: "Answer a few quick questions.", image: "", chip: "Request sent", chip_sub: "", icon: "send" }] } },
  trust_strip: { label: "Trust strip", group: "request", Component: ({ block }) => <TrustStrip props={block.props} />, fullBleed: true, defaults: { tone: "sand", items: [{ icon: "badge-check", title: "Verified pros", body: "Identity and license checks" }] } },
  guarantee_panel: { label: "Guarantee panel (photo + checklist)", group: "request", Component: ({ block, mode }) => <GuaranteePanel props={block.props} mode={mode} />, fullBleed: true, defaults: { eyebrow: "Our promise", headline: "Hire with confidence", lede: "", image: "", points: [{ title: "Secure payments", body: "Funds release when work is done" }], badge: { icon: "shield-check", eyebrow: "Protected", title: "Every booking", body: "" }, cta: { label: "Get Estimates" }, secondary_cta: { label: "Trust & Safety", href: "/trust" } } },


  // === GENERIC PRIMITIVES (for custom pages) ===
  heading: { label: "Heading", group: "basic", Component: HeadingBlock, defaults: { level: "h2", text: "Heading text", align: "left" } },
  text: { label: "Text", group: "basic", Component: TextBlock, defaults: { body: "Add your paragraph text here.", align: "left" } },
  button: { label: "Button", group: "basic", Component: ButtonBlock, defaults: { label: "Learn more", href: "/", variant: "primary" } },
  spacer: { label: "Spacer", group: "layout", Component: SpacerBlock, defaults: { size: "md" } },
  divider: { label: "Divider", group: "layout", Component: DividerBlock, defaults: {} },
  image: { label: "Image", group: "media", Component: ImageBlock, defaults: { src: "", alt: "" } },
  gallery: { label: "Gallery", group: "media", Component: GalleryBlock, defaults: { items: [], columns: 3 } },
  video: { label: "Video embed", group: "media", Component: VideoBlock, defaults: { url: "" } },
  cards: { label: "Cards row", group: "layout", Component: CardsBlock, defaults: { columns: 3, cards: [{ title: "Card 1", body: "Description", icon: "star" }, { title: "Card 2", body: "Description", icon: "shield-check" }, { title: "Card 3", body: "Description", icon: "map-pin" }] } },
  accordion: { label: "Accordion", group: "basic", Component: AccordionBlock, defaults: { items: [{ q: "Question 1", a: "Answer 1" }, { q: "Question 2", a: "Answer 2" }] } },
  cta: { label: "Call to action", group: "basic", Component: CTABlock, defaults: { headline: "Ready to start?", body: "", primary_cta: { label: "Get started", href: "/" }, background: "coastal-blue" } },
  custom_html: { label: "Custom HTML", group: "advanced", Component: CustomHTMLBlock, defaults: { html: "<p>Your HTML here.</p>" } },
  hero_search: { label: "Hero + search (system)", group: "layout", Component: HeroSearchBlock, defaults: {}, protected: true },

  category_grid: { label: "Category grid", group: "dynamic", Component: CategoryGridBlock, defaults: { query: { limit: 8, featured_only: true }, columns: 4 } },
  provider_grid: { label: "Provider grid", group: "dynamic", Component: ProviderGridBlock, defaults: { query: { limit: 6, sort: "-rating_avg" }, columns: 3 } },
  service_grid: { label: "Service grid", group: "dynamic", Component: ServiceGridBlock, defaults: { query: { limit: 6, sort: "-rating_avg" }, columns: 3 } },
  pricing_plans: { label: "Pricing plans", group: "dynamic", Component: PricingPlansBlock, defaults: { audience: "providers" } },
  blog_posts: { label: "Blog posts", group: "dynamic", Component: BlogPostsBlock, defaults: { limit: 3, columns: 3 } },
  help_articles: { label: "Help articles", group: "dynamic", Component: HelpArticlesBlock, defaults: { query: { limit: 20 } } },
  faq_list: { label: "FAQ list", group: "dynamic", Component: FaqListBlock, defaults: { query: { limit: 8 } } },

  form: { label: "Form", group: "form", Component: FormBlock, defaults: { title: "Get in touch", form_key: "contact", fields: [{ name: "name", label: "Your name", type: "text", required: true }, { name: "email", label: "Email", type: "email", required: true }, { name: "message", label: "Message", type: "textarea", required: true }], submit_label: "Send message" } },
  reusable_ref: { label: "Reusable block", group: "advanced", Component: ReusableRefBlock, defaults: { reusable_id: "", fallback_label: "Reusable block" } },
};

export function renderBlock(block, mode = "view") {
  const kind = block?.kind;
  const cfg = BLOCK_REGISTRY[kind];
  if (!cfg) {
    return <div className="rounded-lg border-2 border-dashed border-red-300 bg-red-50 p-3 text-xs text-red-800">Unknown block: {kind}</div>;
  }
  const Component = cfg.Component;
  return <Component block={block} mode={mode} />;
}

export function blockDefaults(kind) {
  const cfg = BLOCK_REGISTRY[kind];
  if (!cfg) return {};
  return JSON.parse(JSON.stringify(cfg.defaults || {}));
}

export function isBlockProtected(block) {
  const cfg = BLOCK_REGISTRY[block?.kind];
  return !!(cfg?.protected || block?.props?.protected);
}

// ----- Icon helper for cards block --------------------------------------

function IconByName({ name, className }) {
  const map = {
    "map-pin": MapPin, "users": Users, "shield-check": ShieldCheck, "star": Star, "mail": Mail,
    "sparkles": Sparkles, "palette": Palette, "shapes": Shapes, "mic": Mic,
    "message-circle": MessageCircle, "paw-print": PawPrint, "wrench": Wrench,
    "paintbrush": Paintbrush, "camera": Camera, "truck": Truck, "scissors": Scissors,
    "home": Home, "briefcase": Briefcase, "code": Code, "globe": Globe,
    "book-open": BookOpen, "music": Music, "video": Video, "pen-tool": PenTool,
    "languages": Languages, "bot": Bot, "headphones": HeadphonesIcon, "laptop": Laptop,
    "flower": Flower2, "sofa": Sofa, "package": Package, "zap": Zap, "trees": Trees,
    "car": Car, "heart": Heart, "graduation-cap": GraduationCap, "baby": Baby,
    "cake": Cake, "utensils": Utensils, "dollar-sign": DollarSign, "layers": Layers,
  };
  const Cmp = map[name] || FolderOpen;
  return <Cmp className={className} />;
}

// ----- Section renderer -------------------------------------------------

export function SectionRenderer({ section, mode = "view", innerProps = {} }) {
  const blocks = section?.blocks || [];
  const layout = section?.layout || "container";
  // If ANY block in this section is registered as fullBleed, skip the
  // outer max-w container so those sections can control their own layout.
  const hasFullBleed = blocks.some(b => BLOCK_REGISTRY[b.kind]?.fullBleed);
  if (hasFullBleed) {
    return (
      <div data-testid={`section-${section.id}`} {...innerProps}>
        {blocks.map((b) => (
          <div key={b.id} data-testid={`block-${b.id}`}>
            {renderBlock(b, mode)}
          </div>
        ))}
      </div>
    );
  }
  const containerCls = layout === "two-column"
    ? "grid gap-6 md:grid-cols-[2fr_1fr]"
    : "";
  return (
    <section className="py-8 md:py-12" data-testid={`section-${section.id}`} {...innerProps}>
      <div className={cx("max-w-6xl mx-auto px-4", containerCls)}>
        {blocks.map((b) => (
          <div key={b.id} data-testid={`block-${b.id}`}>
            {renderBlock(b, mode)}
          </div>
        ))}
      </div>
    </section>
  );
}
